import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, X, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PayPalFnfButton from './PayPalFnfButton';

const API = process.env.REACT_APP_BACKEND_URL;
const BANNER_HEIGHT = 44;

export default function PendingPaymentBanner() {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  const fetchPendingOrders = useCallback(async () => {
    const token = localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const pending = response.data.filter(order => order.status === 'pending' && (order.total || 0) > 0);
      setPendingOrders(pending);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingOrders();
    const interval = setInterval(fetchPendingOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingOrders]);

  // Reset dismissed state when navigating to a new page
  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  // Calculate and update time remaining
  useEffect(() => {
    if (pendingOrders.length === 0) return;

    const oldestOrder = pendingOrders.reduce((oldest, order) => 
      new Date(order.created_at) < new Date(oldest.created_at) ? order : oldest
    , pendingOrders[0]);

    const updateTimer = () => {
      const orderTime = new Date(oldestOrder.created_at);
      const deadline = new Date(orderTime.getTime() + 30 * 60 * 1000);
      const now = new Date();
      const diff = deadline - now;
      
      if (diff <= 0) {
        setTimeLeft('EXPIRED');
        return;
      }
      
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const timerInterval = setInterval(updateTimer, 1000);
    return () => clearInterval(timerInterval);
  }, [pendingOrders]);

  // Update visibility and CSS variable for header offset
  useEffect(() => {
    const hiddenPages = ['/login', '/register', '/forgot-password', '/reset-password', '/verify', '/admin'];
    const shouldHide = hiddenPages.some(page => location.pathname.startsWith(page));
    const shouldShow = !loading && pendingOrders.length > 0 && !dismissed && !shouldHide;
    
    setIsVisible(shouldShow);
    
    // Set CSS variable that pages can use to offset their fixed headers
    document.documentElement.style.setProperty('--banner-offset', shouldShow ? `${BANNER_HEIGHT}px` : '0px');
    
    return () => {
      document.documentElement.style.setProperty('--banner-offset', '0px');
    };
  }, [loading, pendingOrders.length, dismissed, location.pathname]);

  const copyPayPal = () => {
    navigator.clipboard.writeText('payments@shopblkmrkt.com');
    toast.success('PayPal email copied!');
  };

  if (!isVisible) {
    return null;
  }

  const totalDue = pendingOrders.reduce((sum, order) => sum + order.total, 0);
  const isUrgent = timeLeft !== 'EXPIRED' && parseInt(timeLeft.split(':')[0]) < 10;

  return (
    <>
      {/* Spacer div to push content down */}
      <div style={{ height: `${BANNER_HEIGHT}px` }} />
      
      {/* Fixed banner */}
      <div 
        className={`fixed top-0 left-0 right-0 z-[200] py-2 px-4 shadow-lg transition-colors ${
          isUrgent ? 'bg-red-600' : 'bg-amber-600'
        }`}
        style={{ height: `${BANNER_HEIGHT}px` }}
        data-testid="pending-payment-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-4 h-full">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${isUrgent ? 'animate-pulse' : ''}`} />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xs sm:text-sm font-bold text-white truncate">
                {pendingOrders.length} ORDER{pendingOrders.length > 1 ? 'S' : ''} • ${totalDue.toFixed(2)} DUE
              </p>
              <p className="font-mono text-[10px] sm:text-xs text-white/90 hidden sm:block truncate">
                PayPal F&F to <button onClick={copyPayPal} className="underline hover:no-underline">payments@shopblkmrkt.com</button>
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Countdown Timer */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-white ${
              isUrgent ? 'bg-red-700' : 'bg-amber-700'
            }`}>
              <Clock className="w-3 h-3" />
              <span className="font-mono text-xs font-bold">{timeLeft}</span>
            </div>
            
            {/* PAY NOW button — opens F&F confirmation modal, then PayPal */}
            <PayPalFnfButton
              amount={totalDue}
              variant="banner"
              testId="view-pending-orders-btn"
            >
              PAY NOW
            </PayPalFnfButton>
            
            {/* Dismiss Button */}
            <button 
              onClick={() => setDismissed(true)}
              className={`p-1 rounded transition-colors ${
                isUrgent ? 'hover:bg-red-700' : 'hover:bg-amber-700'
              }`}
              aria-label="Dismiss"
              data-testid="dismiss-banner-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
